import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { addTaskFollowUpNote, fetchTaskById } from '../tasksApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './DecisionsPage.css';

export default function TaskDetailPage() {
  const { language, direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: idParam } = useParams();
  const taskId = Number(idParam);
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setError(t('common.noPerm')); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const d = await fetchTaskById(taskId);
        if (!cancelled) setItem(d);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('tasks.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
              <button className="btn btn--ghost" onClick={() => navigate(`${basePath}/tasks`)}><Icon name="chevron-right" size={14} /> {t('common.back')}</button>
              <span className="list-page-card__title"><Icon name="settings" size={18} color="#00334a" style={{ marginLeft: 6 }} />{t('tasks.detailTitle')}</span>
            </div>
          </div>
        </header>
        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t('common.loading')}</div>}
      {!loading && item && (
        <div className="table-card" style={{ padding: '20px 22px' }}>
          <div className="mform">
            <div className="mform__row mform__row--4">
              <div className="mform__field"><label className="mform__label">{t('tasks.lblTitle')}</label><input className="mform__input" value={item.title || ''} disabled /></div>
              <div className="mform__field"><label className="mform__label">{t('tasks.lblAssignee')}</label><input className="mform__input" value={item.assigneeName || '—'} disabled /></div>
              <div className="mform__field"><label className="mform__label">{t('tasks.lblPriority')}</label><input className="mform__input" value={item.priority || ''} disabled /></div>
              <div className="mform__field"><label className="mform__label">{t('tasks.lblStatus')}</label><input className="mform__input" value={item.status || ''} disabled /></div>
            </div>
            <div className="mform__field"><label className="mform__label">{t('tasks.lblDesc')}</label><textarea className="mform__textarea" rows={4} value={item.description || ''} disabled /></div>
            <div className="mform__section-title">{t('tasks.detailTitle')}</div>
            <div className="mform__field"><textarea className="mform__textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)} /></div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                className="btn btn--primary"
                disabled={saving || !note.trim()}
                onClick={async () => {
                  setSaving(true);
                  try {
                    const updated = await addTaskFollowUpNote(item.id, note);
                    setItem(updated);
                    setNote('');
                  } catch (e) {
                    setError(e instanceof Error ? e.message : 'Error');
                  } finally {
                    setSaving(false);
                  }
                }}
              >
                {t('common.save')}
              </button>
            </div>
            <div className="mform__att-list">
              {(item.followUpNotes || []).map((n, i) => (
                <div key={`${n.addedAtUtc}-${i}`} className="mform__att-item">
                  <span className="mform__att-type">{new Date(n.addedAtUtc).toLocaleString(language === 'ar' || language === 'ur' ? 'ar-SA' : 'en-US')}</span>
                  <span className="mform__att-name">{n.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
